import { MotionType } from "./motionType";
class RigidBodyConstructionInfoListInner {
    _wasmInstance;
    _ptr;
    _count;
    _shapeReferences;
    constructor(wasmInstance, ptr, count) {
        this._wasmInstance = wasmInstance;
        this._ptr = ptr;
        this._count = count;
        this._shapeReferences = new Array(count).fill(null);
    }
    dispose() {
        if (this._ptr === 0) {
            return;
        }
        this._wasmInstance.deref()?.deallocateBuffer(this._ptr, 144 /* Constants.RigidBodyConstructionInfoSize */ * this._count);
        this._ptr = 0;
        for (let i = 0; i < this._shapeReferences.length; ++i) {
            const shape = this._shapeReferences[i];
            shape?.removeReference();
        }
        this._shapeReferences.fill(null);
    }
    /**
     * @internal
     */
    get ptr() {
        return this._ptr;
    }
    get count() {
        return this._count;
    }
    getShape(n) {
        return this._shapeReferences[n] ?? null;
    }
    setShape(n, value) {
        if (n < 0 || this._count <= n) {
            throw new RangeError("Index out of range");
        }
        const previousShape = this._shapeReferences[n];
        if (previousShape) {
            previousShape.removeReference();
        }
        this._shapeReferences[n] = value;
        if (value) {
            value.addReference();
        }
    }
}
function RigidBodyConstructionInfoListFinalizer(inner) {
    inner.dispose();
}
const RigidBodyConstructionInfoListRegistryMap = new WeakMap();
/**
 * RigidBodyConstructionInfoList stores the construction information for multiple rigid bodies in a single buffer
 */
export class RigidBodyConstructionInfoList {
    _wasmInstance;
    _uint32Ptr;
    _float32Ptr;
    _uint8Ptr;
    _uint16Ptr;
    _inner;
    /**
     * Creates a new RigidBodyConstructionInfoList
     * @param wasmInstance The BulletWasmInstance to use
     */
    constructor(wasmInstance, count) {
        this._wasmInstance = wasmInstance;
        // Allocate buffer
        const ptr = wasmInstance.allocateBuffer(144 /* Constants.RigidBodyConstructionInfoSize */ * count);
        this._uint32Ptr = wasmInstance.createTypedArray(Uint32Array, ptr, 144 /* Constants.RigidBodyConstructionInfoSize */ / 4 /* Constants.A32BytesPerElement */ * count);
        this._float32Ptr = wasmInstance.createTypedArray(Float32Array, ptr, 144 /* Constants.RigidBodyConstructionInfoSize */ / 4 /* Constants.A32BytesPerElement */ * count);
        this._uint8Ptr = wasmInstance.createTypedArray(Uint8Array, ptr, 144 /* Constants.RigidBodyConstructionInfoSize */ / 1 /* Constants.A8BytesPerElement */ * count);
        this._uint16Ptr = wasmInstance.createTypedArray(Uint16Array, ptr, 144 /* Constants.RigidBodyConstructionInfoSize */ / 2 /* Constants.A16BytesPerElement */ * count);
        this._inner = new RigidBodyConstructionInfoListInner(new WeakRef(wasmInstance), ptr, count);
        // Initialize to default values
        const uint32Ptr = this._uint32Ptr.array;
        const float32Ptr = this._float32Ptr.array;
        const uint8Ptr = this._uint8Ptr.array;
        const uint16Ptr = this._uint16Ptr.array;
        for (let i = 0; i < count; ++i) {
            const offset = i * 144 /* Constants.RigidBodyConstructionInfoSize */;
            // shape
            uint32Ptr[(offset + 0 /* RigidBodyConstructionInfoOffsets.Shape */) / 4 /* Constants.A32BytesPerElement */] = 0;
            // initial_transform
            float32Ptr[(offset + 16 /* RigidBodyConstructionInfoOffsets.InitialTransform */) / 4 /* Constants.A32BytesPerElement */ + 0] = 1;
            float32Ptr[(offset + 16 /* RigidBodyConstructionInfoOffsets.InitialTransform */) / 4 /* Constants.A32BytesPerElement */ + 1] = 0;
            float32Ptr[(offset + 16 /* RigidBodyConstructionInfoOffsets.InitialTransform */) / 4 /* Constants.A32BytesPerElement */ + 2] = 0;
            float32Ptr[(offset + 16 /* RigidBodyConstructionInfoOffsets.InitialTransform */) / 4 /* Constants.A32BytesPerElement */ + 3] = 0;
            float32Ptr[(offset + 16 /* RigidBodyConstructionInfoOffsets.InitialTransform */) / 4 /* Constants.A32BytesPerElement */ + 4] = 0;
            float32Ptr[(offset + 16 /* RigidBodyConstructionInfoOffsets.InitialTransform */) / 4 /* Constants.A32BytesPerElement */ + 5] = 1;
            float32Ptr[(offset + 16 /* RigidBodyConstructionInfoOffsets.InitialTransform */) / 4 /* Constants.A32BytesPerElement */ + 6] = 0;
            float32Ptr[(offset + 16 /* RigidBodyConstructionInfoOffsets.InitialTransform */) / 4 /* Constants.A32BytesPerElement */ + 7] = 0;
            float32Ptr[(offset + 16 /* RigidBodyConstructionInfoOffsets.InitialTransform */) / 4 /* Constants.A32BytesPerElement */ + 8] = 0;
            float32Ptr[(offset + 16 /* RigidBodyConstructionInfoOffsets.InitialTransform */) / 4 /* Constants.A32BytesPerElement */ + 9] = 0;
            float32Ptr[(offset + 16 /* RigidBodyConstructionInfoOffsets.InitialTransform */) / 4 /* Constants.A32BytesPerElement */ + 10] = 1;
            float32Ptr[(offset + 16 /* RigidBodyConstructionInfoOffsets.InitialTransform */) / 4 /* Constants.A32BytesPerElement */ + 11] = 0;
            float32Ptr[(offset + 16 /* RigidBodyConstructionInfoOffsets.InitialTransform */) / 4 /* Constants.A32BytesPerElement */ + 12] = 0;
            float32Ptr[(offset + 16 /* RigidBodyConstructionInfoOffsets.InitialTransform */) / 4 /* Constants.A32BytesPerElement */ + 13] = 0;
            float32Ptr[(offset + 16 /* RigidBodyConstructionInfoOffsets.InitialTransform */) / 4 /* Constants.A32BytesPerElement */ + 14] = 0;
            float32Ptr[(offset + 16 /* RigidBodyConstructionInfoOffsets.InitialTransform */) / 4 /* Constants.A32BytesPerElement */ + 15] = 1;
            // dataMask
            uint16Ptr[(offset + 80 /* RigidBodyConstructionInfoOffsets.DataMask */) / 2 /* Constants.A16BytesPerElement */] = 0x0000;
            // motionType
            uint8Ptr[(offset + 82 /* RigidBodyConstructionInfoOffsets.MotionType */) / 1 /* Constants.A8BytesPerElement */] = MotionType.Dynamic;
            // mass
            float32Ptr[(offset + 84 /* RigidBodyConstructionInfoOffsets.Mass */) / 4 /* Constants.A32BytesPerElement */] = 1.0;
            // localInertia
            float32Ptr[(offset + 88 /* RigidBodyConstructionInfoOffsets.LocalInertia */) / 4 /* Constants.A32BytesPerElement */ + 0] = 0;
            float32Ptr[(offset + 88 /* RigidBodyConstructionInfoOffsets.LocalInertia */) / 4 /* Constants.A32BytesPerElement */ + 1] = 0;
            float32Ptr[(offset + 88 /* RigidBodyConstructionInfoOffsets.LocalInertia */) / 4 /* Constants.A32BytesPerElement */ + 2] = 0;
            // linearDamping
            float32Ptr[(offset + 100 /* RigidBodyConstructionInfoOffsets.LinearDamping */) / 4 /* Constants.A32BytesPerElement */] = 0;
            // angularDamping
            float32Ptr[(offset + 104 /* RigidBodyConstructionInfoOffsets.AngularDamping */) / 4 /* Constants.A32BytesPerElement */] = 0;
            // friction
            float32Ptr[(offset + 108 /* RigidBodyConstructionInfoOffsets.Friction */) / 4 /* Constants.A32BytesPerElement */] = 0.5;
            // restitution
            float32Ptr[(offset + 112 /* RigidBodyConstructionInfoOffsets.Restitution */) / 4 /* Constants.A32BytesPerElement */] = 0.0;
            // linearSleepingThreshold
            float32Ptr[(offset + 116 /* RigidBodyConstructionInfoOffsets.LinearSleepingThreshold */) / 4 /* Constants.A32BytesPerElement */] = 0.0;
            // angularSleepingThreshold
            float32Ptr[(offset + 120 /* RigidBodyConstructionInfoOffsets.AngularSleepingThreshold */) / 4 /* Constants.A32BytesPerElement */] = 1.0;
            // collisionGroup
            uint16Ptr[(offset + 124 /* RigidBodyConstructionInfoOffsets.CollisionGroup */) / 2 /* Constants.A16BytesPerElement */] = 1 << 0;
            // collisionMask
            uint16Ptr[(offset + 126 /* RigidBodyConstructionInfoOffsets.CollisionMask */) / 2 /* Constants.A16BytesPerElement */] = 0xFFFF;
            // additionalDamping
            uint8Ptr[(offset + 128 /* RigidBodyConstructionInfoOffsets.AdditionalDamping */) / 1 /* Constants.A8BytesPerElement */] = +false;
            // noContactResponse
            uint8Ptr[(offset + 129 /* RigidBodyConstructionInfoOffsets.NoContactResponse */) / 1 /* Constants.A8BytesPerElement */] = +false;
            // disableDeactivation
            uint8Ptr[(offset + 130 /* RigidBodyConstructionInfoOffsets.DisableDeactivation */) / 1 /* Constants.A8BytesPerElement */] = +false;
        }
        // finalization registry
        let registry = RigidBodyConstructionInfoListRegistryMap.get(wasmInstance);
        if (registry === undefined) {
            registry = new FinalizationRegistry(RigidBodyConstructionInfoListFinalizer);
            RigidBodyConstructionInfoListRegistryMap.set(wasmInstance, registry);
        }
        registry.register(this, this._inner, this);
    }
    /**
     * Disposes the RigidBodyConstructionInfoList
     */
    dispose() {
        if (this._inner.ptr === 0) {
            return;
        }
        this._inner.dispose();
        const registry = RigidBodyConstructionInfoListRegistryMap.get(this._wasmInstance);
        registry?.unregister(this);
    }
    /**
     * @internal
     */
    get ptr() {
        return this._inner.ptr;
    }
    /**
     * The number of rigid body construction info
     */
    get count() {
        return this._inner.count;
    }
    /**
     * @internal
     */
    getPtr(n) {
        this._nullCheck();
        return this._inner.ptr + n * 144 /* Constants.RigidBodyConstructionInfoSize */;
    }
    _nullCheck() {
        if (this._inner.ptr === 0) {
            throw new Error("Cannot access disposed RigidBodyConstructionInfo");
        }
    }
    /**
     * Get the shape of the rigid body at index n
     * @param n The index of the rigid body
     * @return The shape of the rigid body
     */
    getShape(n) {
        this._nullCheck();
        return this._inner.getShape(n);
    }
    /**
     * Set the shape of the rigid body at index n
     * @param n The index of the rigid body
     * @param value The shape of the rigid body
     */
    setShape(n, value) {
        this._nullCheck();
        this._inner.setShape(n, value);
        const offset = n * 144 /* Constants.RigidBodyConstructionInfoSize */;
        this._uint32Ptr.array[(offset + 0 /* RigidBodyConstructionInfoOffsets.Shape */) / 4 /* Constants.A32BytesPerElement */] = value ? value.ptr : 0;
    }
    /**
     * Get the initial transform of the rigid body at index n
     * @param n The index of the rigid body
     * @param result The matrix to store the result
     * @returns The initial transform of the rigid body
     */
    getInitialTransformToRef(n, result) {
        this._nullCheck();
        const offset = n * 144 /* Constants.RigidBodyConstructionInfoSize */;
        const float32Ptr = this._float32Ptr.array;
        result.set(float32Ptr[(offset + 16 /* RigidBodyConstructionInfoOffsets.InitialTransform */) / 4 /* Constants.A32BytesPerElement */ + 0], float32Ptr[(offset + 16 /* RigidBodyConstructionInfoOffsets.InitialTransform */) / 4 /* Constants.A32BytesPerElement */ + 1], float32Ptr[(offset + 16 /* RigidBodyConstructionInfoOffsets.InitialTransform */) / 4 /* Constants.A32BytesPerElement */ + 2], float32Ptr[(offset + 16 /* RigidBodyConstructionInfoOffsets.InitialTransform */) / 4 /* Constants.A32BytesPerElement */ + 3], float32Ptr[(offset + 16 /* RigidBodyConstructionInfoOffsets.InitialTransform */) / 4 /* Constants.A32BytesPerElement */ + 4], float32Ptr[(offset + 16 /* RigidBodyConstructionInfoOffsets.InitialTransform */) / 4 /* Constants.A32BytesPerElement */ + 5], float32Ptr[(offset + 16 /* RigidBodyConstructionInfoOffsets.InitialTransform */) / 4 /* Constants.A32BytesPerElement */ + 6], float32Ptr[(offset + 16 /* RigidBodyConstructionInfoOffsets.InitialTransform */) / 4 /* Constants.A32BytesPerElement */ + 7], float32Ptr[(offset + 16 /* RigidBodyConstructionInfoOffsets.InitialTransform */) / 4 /* Constants.A32BytesPerElement */ + 8], float32Ptr[(offset + 16 /* RigidBodyConstructionInfoOffsets.InitialTransform */) / 4 /* Constants.A32BytesPerElement */ + 9], float32Ptr[(offset + 16 /* RigidBodyConstructionInfoOffsets.InitialTransform */) / 4 /* Constants.A32BytesPerElement */ + 10], float32Ptr[(offset + 16 /* RigidBodyConstructionInfoOffsets.InitialTransform */) / 4 /* Constants.A32BytesPerElement */ + 11], float32Ptr[(offset + 16 /* RigidBodyConstructionInfoOffsets.InitialTransform */) / 4 /* Constants.A32BytesPerElement */ + 12], float32Ptr[(offset + 16 /* RigidBodyConstructionInfoOffsets.InitialTransform */) / 4 /* Constants.A32BytesPerElement */ + 13], float32Ptr[(offset + 16 /* RigidBodyConstructionInfoOffsets.InitialTransform */) / 4 /* Constants.A32BytesPerElement */ + 14], float32Ptr[(offset + 16 /* RigidBodyConstructionInfoOffsets.InitialTransform */) / 4 /* Constants.A32BytesPerElement */ + 15]);
        return result;
    }
    /**
     * Set the initial transform of the rigid body at index n
     * @param n The index of the rigid body
     * @param value The initial transform of the rigid body
     */
    setInitialTransform(n, value) {
        this._nullCheck();
        const offset = n * 144 /* Constants.RigidBodyConstructionInfoSize */;
        const float32Ptr = this._float32Ptr.array;
        value.copyToArray(float32Ptr, (offset + 16 /* RigidBodyConstructionInfoOffsets.InitialTransform */) / 4 /* Constants.A32BytesPerElement */);
    }
    /**
     * Get the motion type of the rigid body at index n
     * @param n The index of the rigid body
     * @returns The motion type of the rigid body
     */
    getMotionType(n) {
        this._nullCheck();
        const offset = n * 144 /* Constants.RigidBodyConstructionInfoSize */;
        return this._uint8Ptr.array[(offset + 82 /* RigidBodyConstructionInfoOffsets.MotionType */) / 1 /* Constants.A8BytesPerElement */];
    }
    /**
     * Set the motion type of the rigid body at index n
     * @param n The index of the rigid body
     * @param value The motion type of the rigid body
     * @returns The motion type of the rigid body
     */
    setMotionType(n, value) {
        this._nullCheck();
        const offset = n * 144 /* Constants.RigidBodyConstructionInfoSize */;
        this._uint8Ptr.array[(offset + 82 /* RigidBodyConstructionInfoOffsets.MotionType */) / 1 /* Constants.A8BytesPerElement */] = value;
    }
    /**
     * Get the mass of the rigid body at index n
     * @param n The index of the rigid body
     * @returns The mass of the rigid body
     */
    getMass(n) {
        this._nullCheck();
        const offset = n * 144 /* Constants.RigidBodyConstructionInfoSize */;
        return this._float32Ptr.array[(offset + 84 /* RigidBodyConstructionInfoOffsets.Mass */) / 4 /* Constants.A32BytesPerElement */];
    }
    /**
     * Set the mass of the rigid body at index n
     * @param n The index of the rigid body
     * @param value The mass of the rigid body
     */
    setMass(n, value) {
        this._nullCheck();
        const offset = n * 144 /* Constants.RigidBodyConstructionInfoSize */;
        this._float32Ptr.array[(offset + 84 /* RigidBodyConstructionInfoOffsets.Mass */) / 4 /* Constants.A32BytesPerElement */] = value;
    }
    /**
     * Get the local inertia of the rigid body at index n
     * @param n The index of the rigid body
     * @param result The vector to store the result
     * @returns The local inertia of the rigid body
     */
    getLocalInertiaToRef(n, result) {
        this._nullCheck();
        const offset = n * 144 /* Constants.RigidBodyConstructionInfoSize */;
        const maskValue = this._uint16Ptr.array[(offset + 80 /* RigidBodyConstructionInfoOffsets.DataMask */) / 2 /* Constants.A16BytesPerElement */];
        if ((maskValue & 1 /* ConstructionInfoDataMask.LocalInertia */) === 0) {
            return null;
        }
        const float32Ptr = this._float32Ptr.array;
        result.set(float32Ptr[(offset + 88 /* RigidBodyConstructionInfoOffsets.LocalInertia */) / 4 /* Constants.A32BytesPerElement */ + 0], float32Ptr[(offset + 88 /* RigidBodyConstructionInfoOffsets.LocalInertia */) / 4 /* Constants.A32BytesPerElement */ + 1], float32Ptr[(offset + 88 /* RigidBodyConstructionInfoOffsets.LocalInertia */) / 4 /* Constants.A32BytesPerElement */ + 2]);
        return result;
    }
    /**
     * Set the local inertia of the rigid body at index n
     *
     * If the local inertia is not set, it will be calculated from the shape
     * @param n The index of the rigid body
     * @param value The local inertia of the rigid body
     */
    setLocalInertia(n, value) {
        this._nullCheck();
        const offset = n * 144 /* Constants.RigidBodyConstructionInfoSize */;
        if (value === null) {
            this._uint16Ptr.array[(offset + 80 /* RigidBodyConstructionInfoOffsets.DataMask */) / 2 /* Constants.A16BytesPerElement */] &= ~1 /* ConstructionInfoDataMask.LocalInertia */;
            return;
        }
        this._uint16Ptr.array[(offset + 80 /* RigidBodyConstructionInfoOffsets.DataMask */) / 2 /* Constants.A16BytesPerElement */] |= 1 /* ConstructionInfoDataMask.LocalInertia */;
        const float32Ptr = this._float32Ptr.array;
        float32Ptr[(offset + 88 /* RigidBodyConstructionInfoOffsets.LocalInertia */) / 4 /* Constants.A32BytesPerElement */ + 0] = value.x;
        float32Ptr[(offset + 88 /* RigidBodyConstructionInfoOffsets.LocalInertia */) / 4 /* Constants.A32BytesPerElement */ + 1] = value.y;
        float32Ptr[(offset + 88 /* RigidBodyConstructionInfoOffsets.LocalInertia */) / 4 /* Constants.A32BytesPerElement */ + 2] = value.z;
    }
    /**
     * Get linear damping of the rigid body at index n
     * @param n The index of the rigid body
     * @returns The linear damping of the rigid body
     */
    getLinearDamping(n) {
        this._nullCheck();
        const offset = n * 144 /* Constants.RigidBodyConstructionInfoSize */;
        return this._float32Ptr.array[(offset + 100 /* RigidBodyConstructionInfoOffsets.LinearDamping */) / 4 /* Constants.A32BytesPerElement */];
    }
    /**
     * Set linear damping of the rigid body at index n
     * @param n The index of the rigid body
     * @param value The linear damping of the rigid body
     */
    setLinearDamping(n, value) {
        this._nullCheck();
        const offset = n * 144 /* Constants.RigidBodyConstructionInfoSize */;
        this._float32Ptr.array[(offset + 100 /* RigidBodyConstructionInfoOffsets.LinearDamping */) / 4 /* Constants.A32BytesPerElement */] = value;
    }
    /**
     * Get angular damping of the rigid body at index n
     * @param n The index of the rigid body
     * @returns The angular damping of the rigid body
     */
    getAngularDamping(n) {
        this._nullCheck();
        const offset = n * 144 /* Constants.RigidBodyConstructionInfoSize */;
        return this._float32Ptr.array[(offset + 104 /* RigidBodyConstructionInfoOffsets.AngularDamping */) / 4 /* Constants.A32BytesPerElement */];
    }
    /**
     * Set angular damping of the rigid body at index n
     * @param n The index of the rigid body
     * @param value The angular damping of the rigid body
     */
    setAngularDamping(n, value) {
        this._nullCheck();
        const offset = n * 144 /* Constants.RigidBodyConstructionInfoSize */;
        this._float32Ptr.array[(offset + 104 /* RigidBodyConstructionInfoOffsets.AngularDamping */) / 4 /* Constants.A32BytesPerElement */] = value;
    }
    /**
     * Get friction of the rigid body at index n
     * @param n The index of the rigid body
     * @returns The friction of the rigid body
     */
    getFriction(n) {
        this._nullCheck();
        const offset = n * 144 /* Constants.RigidBodyConstructionInfoSize */;
        return this._float32Ptr.array[(offset + 108 /* RigidBodyConstructionInfoOffsets.Friction */) / 4 /* Constants.A32BytesPerElement */];
    }
    /**
     * Set friction of the rigid body at index n
     * @param n The index of the rigid body
     * @param value The friction of the rigid body
     */
    setFriction(n, value) {
        this._nullCheck();
        const offset = n * 144 /* Constants.RigidBodyConstructionInfoSize */;
        this._float32Ptr.array[(offset + 108 /* RigidBodyConstructionInfoOffsets.Friction */) / 4 /* Constants.A32BytesPerElement */] = value;
    }
    /**
     * Get restitution of the rigid body at index n
     * @param n The index of the rigid body
     * @returns The restitution of the rigid body
     */
    getRestitution(n) {
        this._nullCheck();
        const offset = n * 144 /* Constants.RigidBodyConstructionInfoSize */;
        return this._float32Ptr.array[(offset + 112 /* RigidBodyConstructionInfoOffsets.Restitution */) / 4 /* Constants.A32BytesPerElement */];
    }
    /**
     * Set restitution of the rigid body at index n
     * @param n The index of the rigid body
     * @param value The restitution of the rigid body
     */
    setRestitution(n, value) {
        this._nullCheck();
        const offset = n * 144 /* Constants.RigidBodyConstructionInfoSize */;
        this._float32Ptr.array[(offset + 112 /* RigidBodyConstructionInfoOffsets.Restitution */) / 4 /* Constants.A32BytesPerElement */] = value;
    }
    /**
     * Get linear sleeping threshold of the rigid body at index n
     * @param n The index of the rigid body
     * @returns The linear sleeping threshold of the rigid body
     */
    getLinearSleepingThreshold(n) {
        this._nullCheck();
        const offset = n * 144 /* Constants.RigidBodyConstructionInfoSize */;
        return this._float32Ptr.array[(offset + 116 /* RigidBodyConstructionInfoOffsets.LinearSleepingThreshold */) / 4 /* Constants.A32BytesPerElement */];
    }
    /**
     * Set linear sleeping threshold of the rigid body at index n
     * @param n The index of the rigid body
     * @param value The linear sleeping threshold of the rigid body
     */
    setLinearSleepingThreshold(n, value) {
        this._nullCheck();
        const offset = n * 144 /* Constants.RigidBodyConstructionInfoSize */;
        this._float32Ptr.array[(offset + 116 /* RigidBodyConstructionInfoOffsets.LinearSleepingThreshold */) / 4 /* Constants.A32BytesPerElement */] = value;
    }
    /**
     * Get angular sleeping threshold of the rigid body at index n
     * @param n The index of the rigid body
     * @returns The angular sleeping threshold of the rigid body
     */
    getAngularSleepingThreshold(n) {
        this._nullCheck();
        const offset = n * 144 /* Constants.RigidBodyConstructionInfoSize */;
        return this._float32Ptr.array[(offset + 120 /* RigidBodyConstructionInfoOffsets.AngularSleepingThreshold */) / 4 /* Constants.A32BytesPerElement */];
    }
    /**
     * Set angular sleeping threshold of the rigid body at index n
     * @param n The index of the rigid body
     * @param value The angular sleeping threshold of the rigid body
     */
    setAngularSleepingThreshold(n, value) {
        this._nullCheck();
        const offset = n * 144 /* Constants.RigidBodyConstructionInfoSize */;
        this._float32Ptr.array[(offset + 120 /* RigidBodyConstructionInfoOffsets.AngularSleepingThreshold */) / 4 /* Constants.A32BytesPerElement */] = value;
    }
    /**
     * Get collision group of the rigid body at index n
     *
     * collision group is stored as 16-bit unsigned integer
     * @param n The index of the rigid body
     * @return The collision group of the rigid body
     */
    getCollisionGroup(n) {
        this._nullCheck();
        const offset = n * 144 /* Constants.RigidBodyConstructionInfoSize */;
        return this._uint16Ptr.array[(offset + 124 /* RigidBodyConstructionInfoOffsets.CollisionGroup */) / 2 /* Constants.A16BytesPerElement */];
    }
    /**
     * Set collision group of the rigid body at index n
     *
     * collision group is stored as 16-bit unsigned integer
     * @param n The index of the rigid body
     * @param value The collision group of the rigid body
     */
    setCollisionGroup(n, value) {
        this._nullCheck();
        const offset = n * 144 /* Constants.RigidBodyConstructionInfoSize */;
        this._uint16Ptr.array[(offset + 124 /* RigidBodyConstructionInfoOffsets.CollisionGroup */) / 2 /* Constants.A16BytesPerElement */] = value;
    }
    /**
     * Get collision mask of the rigid body at index n
     *
     * collision mask is stored as 16-bit unsigned integer
     * @param n The index of the rigid body
     * @return The collision mask of the rigid body
     */
    getCollisionMask(n) {
        this._nullCheck();
        const offset = n * 144 /* Constants.RigidBodyConstructionInfoSize */;
        return this._uint16Ptr.array[(offset + 126 /* RigidBodyConstructionInfoOffsets.CollisionMask */) / 2 /* Constants.A16BytesPerElement */];
    }
    /**
     * Set collision mask of the rigid body at index n
     *
     * collision mask is stored as 16-bit unsigned integer
     * @param n The index of the rigid body
     * @param value The collision mask of the rigid body
     */
    setCollisionMask(n, value) {
        this._nullCheck();
        const offset = n * 144 /* Constants.RigidBodyConstructionInfoSize */;
        this._uint16Ptr.array[(offset + 126 /* RigidBodyConstructionInfoOffsets.CollisionMask */) / 2 /* Constants.A16BytesPerElement */] = value;
    }
    /**
     * Get additional damping of the rigid body at index n
     * @param n The index of the rigid body
     * @returns The additional damping of the rigid body
     */
    getAdditionalDamping(n) {
        this._nullCheck();
        const offset = n * 144 /* Constants.RigidBodyConstructionInfoSize */;
        return !!this._uint8Ptr.array[(offset + 128 /* RigidBodyConstructionInfoOffsets.AdditionalDamping */) / 1 /* Constants.A8BytesPerElement */];
    }
    /**
     * Set additional damping of the rigid body at index n
     * @param n The index of the rigid body
     * @param value The additional damping of the rigid body
     */
    setAdditionalDamping(n, value) {
        this._nullCheck();
        const offset = n * 144 /* Constants.RigidBodyConstructionInfoSize */;
        this._uint8Ptr.array[(offset + 128 /* RigidBodyConstructionInfoOffsets.AdditionalDamping */) / 1 /* Constants.A8BytesPerElement */] = +value;
    }
    /**
     * Get no contact response of the rigid body at index n
     * @param n The index of the rigid body
     * @returns The no contact response of the rigid body
     */
    getNoContactResponse(n) {
        this._nullCheck();
        const offset = n * 144 /* Constants.RigidBodyConstructionInfoSize */;
        return !!this._uint8Ptr.array[(offset + 129 /* RigidBodyConstructionInfoOffsets.NoContactResponse */) / 1 /* Constants.A8BytesPerElement */];
    }
    /**
     * Set no contact response of the rigid body at index n
     * @param n The index of the rigid body
     * @param value The no contact response of the rigid body
     */
    setNoContactResponse(n, value) {
        this._nullCheck();
        const offset = n * 144 /* Constants.RigidBodyConstructionInfoSize */;
        this._uint8Ptr.array[(offset + 129 /* RigidBodyConstructionInfoOffsets.NoContactResponse */) / 1 /* Constants.A8BytesPerElement */] = +value;
    }
    /**
     * Get disable deactivation of the rigid body at index n
     * @param n The index of the rigid body
     * @returns The disable deactivation of the rigid body
     */
    getDisableDeactivation(n) {
        this._nullCheck();
        const offset = n * 144 /* Constants.RigidBodyConstructionInfoSize */;
        return !!this._uint8Ptr.array[(offset + 130 /* RigidBodyConstructionInfoOffsets.DisableDeactivation */) / 1 /* Constants.A8BytesPerElement */];
    }
    /**
     * Set disable deactivation of the rigid body at index n
     * @param n The index of the rigid body
     * @param value The disable deactivation of the rigid body
     */
    setDisableDeactivation(n, value) {
        this._nullCheck();
        const offset = n * 144 /* Constants.RigidBodyConstructionInfoSize */;
        this._uint8Ptr.array[(offset + 130 /* RigidBodyConstructionInfoOffsets.DisableDeactivation */) / 1 /* Constants.A8BytesPerElement */] = +value;
    }
}
